/**
 * @fileoverview Sales CRM prospect and approval query keys.
 * @description Every backend prospect, prospect-metadata and approval query sits under the
 * `['sales', 'crm']` prefix, so invalidating it refreshes the backlog, its KPI cards, any open
 * prospect and the review list together; commands invalidate the wider `['sales']` prefix.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { PROSPECT_METADATA_QUERY_KEY } from '@/shared/constants/directory-options-query-keys.constants'
import { SALES_DASHBOARD_QUERY_KEY } from '@/shared/constants/sales-query-keys.constants'

import type { ApprovalListFilters } from '@/features/sales/types/approval.types'
import type { MyPageTargetFilters } from '@/features/sales/types/my-page.types'
import type { ProspectListFilters } from '@/features/sales/types/prospect.types'
import type { ClientListFilters } from '@/features/sales/types/sales-client.types'
import type { SalesPerformanceQuery } from '@/features/sales/types/sales-performance.types'
import type { SalesTargetPeriodType } from '@/features/sales/types/sales-target.types'

/**
 * @description The prefix every sales query shares — the backend Sales CRM queries below and the
 * mock-only tenant, target and roster queries. A Sales CRM command invalidates all of it, so while
 * the mock API is on the mock Tenant/Target model behind each pipeline row refreshes too (against
 * the real backend those queries never run, so there's nothing extra to refetch).
 */
export const SALES_QUERY_KEY = ['sales'] as const

/** @description The shared prefix of every Sales CRM query key. */
export const SALES_CRM_QUERY_KEY = ['sales', 'crm'] as const

/** @description Each Sales CRM query's key. */
export const SALES_CRM_QUERY_KEYS = {
  prospects: (filters: ProspectListFilters) =>
    [...SALES_CRM_QUERY_KEY, 'prospects', filters] as const,
  summary: [...SALES_CRM_QUERY_KEY, 'summary'] as const,
  prospect: (prospectId: string) => [...SALES_CRM_QUERY_KEY, 'prospect', prospectId] as const,
  eligibleOwners: [...SALES_CRM_QUERY_KEY, 'eligible-owners'] as const,
  eligibleAssignees: [...SALES_CRM_QUERY_KEY, 'eligible-assignees'] as const,
  targetPeriods: (type: SalesTargetPeriodType) =>
    [...SALES_CRM_QUERY_KEY, 'target-periods', type] as const,
  approvalSummary: [...SALES_CRM_QUERY_KEY, 'approval-summary'] as const,
  metadata: PROSPECT_METADATA_QUERY_KEY,
  myPageTargets: (filters: MyPageTargetFilters) =>
    [...SALES_CRM_QUERY_KEY, 'my-page-targets', filters] as const,
  myPageSummary: [...SALES_CRM_QUERY_KEY, 'my-page-summary'] as const,
  myPageTarget: (targetId: string) => [...SALES_CRM_QUERY_KEY, 'my-page-target', targetId] as const,
  myPageReassignees: (targetId: string) =>
    [...SALES_CRM_QUERY_KEY, 'my-page-reassignees', targetId] as const,
  organisationDetails: [...SALES_CRM_QUERY_KEY, 'organisation-details'] as const,
  performanceSummary: (query: SalesPerformanceQuery) =>
    [...SALES_CRM_QUERY_KEY, 'performance-summary', query] as const,
  performanceRanking: (query: SalesPerformanceQuery) =>
    [...SALES_CRM_QUERY_KEY, 'performance-ranking', query] as const,
  performancePending: (query: SalesPerformanceQuery) =>
    [...SALES_CRM_QUERY_KEY, 'performance-pending', query] as const,
  salesDashboard: SALES_DASHBOARD_QUERY_KEY,
  clientSummary: [...SALES_CRM_QUERY_KEY, 'client-summary'] as const,
  clientFilterOptions: [...SALES_CRM_QUERY_KEY, 'client-filter-options'] as const,
  clients: (filters: ClientListFilters) => [...SALES_CRM_QUERY_KEY, 'clients', filters] as const,
  client: (tenantId: string) => [...SALES_CRM_QUERY_KEY, 'client', tenantId] as const,
  approvals: (filters: ApprovalListFilters) =>
    [...SALES_CRM_QUERY_KEY, 'approvals', filters] as const,
  approval: (approvalRequestId: string) =>
    [...SALES_CRM_QUERY_KEY, 'approval', approvalRequestId] as const,
}
